import { z } from 'zod';
import { isoDateSchema } from './common.js';

/**
 * Rust-specific server data: the save identity, the plugin framework and the two wipes.
 * Everything here is read from the server itself (its startup command, egg variables and
 * files); see docs/DESIGN.md §7.
 */

/** Rust's own default when the startup command doesn't pass +server.identity. */
export const RUST_DEFAULT_IDENTITY = 'my_server_identity';

const IDENTITY_RE = /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,63}$/;

/**
 * The save identity from a rendered startup command (+server.identity "name" or
 * +server.identity name). Saves live in server/<identity>/. Returns null when the value
 * isn't a plain folder name, so nothing is ever wiped outside that folder.
 */
export function rustIdentity(rendered: string): { identity: string; fromStartup: boolean } | null {
  const m = /\+server\.identity\s+(?:"([^"]*)"|'([^']*)'|(\S+))/.exec(rendered);
  if (!m) return { identity: RUST_DEFAULT_IDENTITY, fromStartup: false };
  const identity = m[1] ?? m[2] ?? m[3] ?? '';
  return IDENTITY_RE.test(identity) && !identity.includes('..')
    ? { identity, fromStartup: true }
    : null;
}

/** The plugins folder for a FRAMEWORK value; null for vanilla (no plugin support). */
export function rustPluginFolder(framework: string | null): string | null {
  switch ((framework ?? '').toLowerCase()) {
    case 'oxide':
    case 'umod':
      return '/oxide/plugins';
    case 'carbon':
      return '/carbon/plugins';
    default:
      return null;
  }
}

/** An Oxide/Carbon plugin file name: one C# file, no folders. */
export const oxidePluginNameSchema = z
  .string()
  .max(128)
  .regex(
    /^[A-Za-z0-9_][A-Za-z0-9_.-]*\.cs$/,
    'Plugins are single .cs files (letters, digits, _ . -).',
  );

export const OXIDE_PLUGIN_MAX_BYTES = 4 * 1024 * 1024;

/** A variable the wipe dialog can change, when the egg has it and lets users see it. */
const wipeVariableSchema = z
  .object({ env: z.string(), value: z.string(), editable: z.boolean(), rules: z.string() })
  .nullable();

export const rustServerSchema = z.object({
  /** Where saves live, from the startup command; null if it isn't a plain folder name. */
  identity: z.string().nullable(),
  /** False when the startup command doesn't set +server.identity (Rust's default is used). */
  identityFromStartup: z.boolean(),
  saveFolder: z.string().nullable(),
  /** The FRAMEWORK variable (vanilla, oxide, carbon), when the egg has one users can see. */
  framework: z.string().nullable(),
  pluginFolder: z.string().nullable(),
  world: z.object({ seed: wipeVariableSchema, size: wipeVariableSchema }),
  /**
   * What each wipe would delete right now, read from the save folder by hearthd. Null when
   * the caller can't list files or the identity is unusable; `error` says why it's missing.
   */
  wipe: z.object({ map: z.array(z.string()), full: z.array(z.string()) }).nullable(),
  wipeError: z.string().nullable(),
});
export type RustServer = z.infer<typeof rustServerSchema>;

export const WIPE_KINDS = ['map', 'full'] as const;
export type WipeKind = (typeof WIPE_KINDS)[number];

export const rustWipeSchema = z.object({
  kind: z.enum(WIPE_KINDS),
  /** The server's name, typed by the user; checked by the API. */
  confirm: z.string().max(191),
  /** Optional new map: written to the egg's WORLD_SEED / WORLD_SIZE before wiping. */
  worldSeed: z.string().trim().max(32).optional(),
  worldSize: z.string().trim().max(8).optional(),
});

export const rustWipeResultSchema = z.object({
  folder: z.string(),
  deleted: z.array(z.string()),
  /** Variables changed for the new map (names only). */
  changed: z.array(z.string()),
  daemonSynced: z.boolean(),
  daemonError: z.string().nullable(),
});

export const oxidePluginSchema = z.object({
  name: z.string(),
  size: z.number(),
  modifiedAt: isoDateSchema,
  /** Protected by the egg's denylist: listed, but can't be replaced or removed. */
  denied: z.boolean(),
});

export const oxidePluginListSchema = z.object({
  framework: z.string().nullable(),
  /** Null for vanilla servers: there is nowhere for plugins to go. */
  folder: z.string().nullable(),
  /** False until the framework has created its folder (first start with it installed). */
  folderExists: z.boolean(),
  plugins: z.array(oxidePluginSchema),
});
export type OxidePluginList = z.infer<typeof oxidePluginListSchema>;

export const oxidePluginParamsSchema = z.object({
  server: z.string(),
  name: oxidePluginNameSchema,
});

export const oxidePluginUploadQuerySchema = z.object({
  /** Replace an existing plugin of the same name (needs file.update as well). */
  replace: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
